/**
 * Claiming a pairing code — the only way a screen ever comes into existence.
 *
 * Everything here is deliberately free of React and of the network except for the
 * one function that makes the request, because the interesting part of claiming a
 * code is not the POST. It is what the panel says when the code does not work,
 * and there are exactly three ways that happens to a real person standing in a
 * shop with a remote in their hand:
 *
 *   they mistyped it        — the code is fine, the characters are wrong
 *   it expired              — codes live PAIRING_TTL_SEC; the fix is on the TV
 *   somebody already used it — a code is one-shot by design
 *
 * Those three need three different sentences. "Not found" for all of them sends
 * someone to check the wrong thing, and the wrong thing is usually a ladder.
 *
 * The API distinguishes them by status where it can (409 for a code already
 * spent) but cannot always: an unknown code and an expired one are both absent
 * from the table by the time the request lands, and `ErrorCode` has no member
 * that separates them. So the mapping reads the server's own wording as well as
 * its status, and falls back to the status bucket — never to a blank — when the
 * wording is something this build has not seen.
 */

import {
  PAIRING_CODE_ALPHABET,
  PAIRING_CODE_LENGTH,
  PAIRING_TTL_SEC,
} from '@pbtv/shared';
import type { ScreenClaim } from '@pbtv/shared';
import { errorMessage, isApiError, requestJson } from './api.js';

// ---------------------------------------------------------------------------
// The code itself
// ---------------------------------------------------------------------------

/**
 * Characters a pairing code can never contain, derived from the alphabet rather
 * than written out, so widening or narrowing `PAIRING_CODE_ALPHABET` cannot leave
 * this panel telling people something untrue.
 */
export const EXCLUDED_CODE_CHARACTERS: readonly string[] = [
  ...'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789',
].filter((character) => !PAIRING_CODE_ALPHABET.includes(character));

/** "B, I, O, S, Z, 0, 1 or 5" — for a hint a person can actually check against. */
export function excludedCharacterList(): string {
  const characters = [...EXCLUDED_CODE_CHARACTERS];
  const last = characters.pop();
  if (last === undefined) return '';
  if (characters.length === 0) return last;
  return `${characters.join(', ')} or ${last}`;
}

/** How long a code lives, in whole minutes, for copy that cannot drift. */
export const PAIRING_TTL_MINUTES = Math.round(PAIRING_TTL_SEC / 60);

/**
 * What someone typed, folded to what the TV could actually have shown.
 *
 * Lowercase is upper-cased, and anything outside the alphabet is dropped rather
 * than rejected: the alphabet exists precisely so no character is ambiguous on a
 * screen across a counter, so a character outside it was never on that screen and
 * is a slipped finger. Spaces and dashes people add for readability disappear the
 * same way.
 */
export function normaliseUserCode(raw: string): string {
  return [...raw.toUpperCase()]
    .filter((character) => PAIRING_CODE_ALPHABET.includes(character))
    .slice(0, PAIRING_CODE_LENGTH)
    .join('');
}

export function isCompleteUserCode(code: string): boolean {
  return code.length === PAIRING_CODE_LENGTH;
}

// ---------------------------------------------------------------------------
// The request
// ---------------------------------------------------------------------------

export interface ClaimedScreen {
  screenId: string;
  name: string;
}

function firstString(...values: readonly unknown[]): string | null {
  for (const value of values) {
    if (typeof value === 'string' && value.trim() !== '') return value;
  }
  return null;
}

function screenIdentity(value: unknown): ClaimedScreen | null {
  if (typeof value !== 'object' || value === null) return null;
  const record = value as Record<string, unknown>;

  // A fleet row nests identity under `screen`; a flatter shape carries it
  // directly. Whichever arrives, the id and the name are what the panel wants.
  const nested = screenIdentity(record['screen']);
  if (nested !== null) return nested;

  const screenId = firstString(record['screenId'], record['id']);
  if (screenId === null) return null;

  return { screenId, name: firstString(record['name']) ?? '' };
}

/**
 * The new screen's identity, if the answer carried one.
 *
 * `packages/shared` pins the claim REQUEST and says nothing about the response,
 * so the response is treated as advisory: a claim that succeeded has succeeded,
 * and an envelope this build does not recognise costs a confirmation sentence,
 * never the claim itself. That is the opposite of how the panel reads a GET,
 * where a shape it cannot parse really is a failure.
 */
export function readClaimedScreen(payload: unknown): ClaimedScreen | null {
  if (typeof payload !== 'object' || payload === null) return null;
  const record = payload as Record<string, unknown>;

  return (
    screenIdentity(record['detail']) ?? screenIdentity(record['screen']) ?? screenIdentity(record)
  );
}

export async function claimScreen(input: ScreenClaim): Promise<ClaimedScreen | null> {
  const payload = await requestJson('/admin/screens/claim', { method: 'POST', body: input });
  return readClaimedScreen(payload);
}

// ---------------------------------------------------------------------------
// What to say when it does not work
// ---------------------------------------------------------------------------

export type ClaimFailureKind =
  | 'unknown-code'
  | 'expired-code'
  | 'already-claimed'
  | 'unknown-store'
  | 'forbidden-store'
  | 'rate-limited'
  | 'not-shipped'
  | 'other';

export interface ClaimFailure {
  kind: ClaimFailureKind;
  title: string;
  message: string;
  /** True when retyping the same code cannot work — the TV has to make a new one. */
  needsNewCode: boolean;
  /** Which field to send the cursor back to, when one is clearly at fault. */
  field: 'userCode' | 'storeId' | null;
}

/**
 * How to get a fresh code, in the words of somebody standing in front of the TV.
 * Every failure that needs a new code ends with this, because "the code is no
 * longer valid" without it leaves a person holding a remote and no next step.
 */
export const RESTART_APP_INSTRUCTIONS =
  'On the stick, force-stop the app — Settings → Applications → Manage Installed Applications → Pizza Bros TV → Force stop — then open it again. It shows a new code straight away.';

/** Wording that means the code aged out, in either the server's voice or a gateway's. */
const EXPIRED_WORDING = /expire|no longer valid|timed out|too old/i;
/** Wording that means the code was spent, by this person or by someone else. */
const CLAIMED_WORDING = /already|claimed|in use|been used|spent/i;

function messageOf(error: unknown): string {
  return isApiError(error) ? error.message : errorMessage(error);
}

/**
 * Reads the failure the API sent and returns something a person can act on.
 *
 * The order matters: expiry is checked before "already claimed" because a code
 * that expired after being claimed is, to the person retyping it, expired — the
 * fix is the same restart either way, and naming the wrong cause invites them to
 * go looking for a screen that was never created.
 */
export function describeClaimFailure(error: unknown): ClaimFailure {
  const message = messageOf(error);

  if (!isApiError(error)) {
    return {
      kind: 'other',
      title: 'The claim did not go through',
      message,
      needsNewCode: false,
      field: null,
    };
  }

  if (error.isTransport) {
    return {
      kind: 'other',
      title: 'Could not reach the server',
      message: `${message} The code stays valid for ${PAIRING_TTL_MINUTES} minutes, so there is time to try again.`,
      needsNewCode: false,
      field: null,
    };
  }

  if (error.status === 429) {
    const wait =
      error.retryAfterSec === null
        ? 'Wait a few minutes and try again.'
        : `Wait ${error.retryAfterSec} seconds and try again.`;
    return {
      kind: 'rate-limited',
      title: 'Too many attempts',
      message: `${message} ${wait}`,
      needsNewCode: false,
      field: null,
    };
  }

  if (error.status === 403) {
    return {
      kind: 'forbidden-store',
      title: 'That store is not yours to claim into',
      message: `${message} Pick a store you manage, or ask an administrator to claim this TV for you.`,
      needsNewCode: false,
      field: 'storeId',
    };
  }

  // A scheduled milestone boundary, not a fault — and worth saying plainly, so
  // nobody spends an afternoon deciding their pairing code is broken.
  if (error.status === 501) {
    return {
      kind: 'not-shipped',
      title: 'Claiming is not switched on yet',
      message,
      needsNewCode: false,
      field: null,
    };
  }

  if (EXPIRED_WORDING.test(message)) {
    return {
      kind: 'expired-code',
      title: 'That code has expired',
      message: `A pairing code lasts ${PAIRING_TTL_MINUTES} minutes. ${RESTART_APP_INSTRUCTIONS}`,
      needsNewCode: true,
      field: 'userCode',
    };
  }

  if (error.status === 409 || CLAIMED_WORDING.test(message)) {
    return {
      kind: 'already-claimed',
      title: 'That code has already been used',
      message: `A pairing code works once. If this TV is already in the list, it is paired and there is nothing left to do; if it was claimed by mistake, un-pair that screen first. ${RESTART_APP_INSTRUCTIONS}`,
      needsNewCode: true,
      field: 'userCode',
    };
  }

  // The claim carries two identifiers, and the route checks the store first, so a
  // 404 is not always about the code. Saying "no TV is showing that code" when
  // the store was the problem sends somebody to stare at a television.
  if (error.status === 404 && /store/i.test(message)) {
    return {
      kind: 'unknown-store',
      title: 'That store is no longer there',
      message: `${message} The store list on this page was read when it opened — reload and try again.`,
      needsNewCode: false,
      field: 'storeId',
    };
  }

  if (error.status === 404 || error.status === 400) {
    return {
      kind: 'unknown-code',
      title: 'No TV is showing that code',
      message: `Check the code against the television one character at a time. A code is ${PAIRING_CODE_LENGTH} characters and never contains ${excludedCharacterList()}. If it still will not take, it has aged out. ${RESTART_APP_INSTRUCTIONS}`,
      needsNewCode: false,
      field: 'userCode',
    };
  }

  return {
    kind: 'other',
    title: 'The claim did not go through',
    message,
    needsNewCode: false,
    field: null,
  };
}
